import classNames from 'classnames';
import type { TFunction } from 'i18next';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IPredicate, User } from '@feathr/blackbox';
import { CampaignClass } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import { DatePicker, Input, NumberInput, Select } from '@feathr/components';
import { useUser } from '@feathr/extender/state';
import { moment, TimeFormat, timezoneAbbr } from '@feathr/hooks';

import type { ICollectionOption } from '../../PredicateIdValueSelect';
import type { TCollection } from '../../PredicateIdValueSelect/PredicateIdValueSelect';
import PredicateIdValueSelect from '../../PredicateIdValueSelect/PredicateIdValueSelect';
import PredicateListValueSelect from '../../PredicateListValueSelect';
import PredicateTextValueInput from '../../PredicateTextValueInput';
import type { IRelativeDateOptions, TOptionsGroup } from '../utils';
import { getRelativeDateOptionsMap, isEmailTrigger, isExistenceComparison } from '../utils';

import * as styles from './ValueInput.css';

interface IBooleanOption {
  id: boolean;
  name: string;
}

interface IProps {
  disabled?: boolean;
  excludeIds?: string[];
  handleChangeValue: (value: IPredicate['value']) => void;
  optionsGroup: TOptionsGroup;
  predicate: IPredicate;
}

interface IInputParams {
  predicate: IPredicate;
  excludeIds: string[];
  disabled: boolean;
  t: TFunction;
  optionsGroup: TOptionsGroup;
  handleChangeValue: (value: IPredicate['value']) => void;
  selectedRelativeTimeOptionId: string;
  setSelectedRelativeTimeOptionId: (id: string) => void;
  relativeDateOptionsMap: IRelativeDateOptions;
  user: User;
}

/**
 * Campaign classes offered in a "campaign is/in" predicate value dropdown.
 * Autosend classes are withheld from email-campaign triggers so an autosend
 * can't be used in the trigger condition of another autosend; every other
 * context offers them. Mailrail drips aren't offered anywhere: a drip migrated
 * from Pinpoint has breadcrumbs from before its migration whose cpn_id is a
 * step campaign rather than the drip, so a predicate on the drip misses them.
 * This restriction should be lifted after the crumbs are rewritten.
 */
export function getAllowedCampaignClasses(optionsGroup: TOptionsGroup): CampaignClass[] {
  const allowedClasses: CampaignClass[] = [
    CampaignClass.Conversation,
    CampaignClass.LandingPage,
    CampaignClass.EmailList,
    CampaignClass.Lookalike,
    CampaignClass.SeedSegment,
    CampaignClass.Affinity,
    CampaignClass.Segment,
    CampaignClass.Search,
    CampaignClass.MailrailSingleSend,
    CampaignClass.MobileGeoFencing,
    CampaignClass.MobileGeoFenceRetargeting,
    CampaignClass.Facebook,
    CampaignClass.TrackedLink,
    CampaignClass.EmailListFacebook,
    CampaignClass.Referral,
    CampaignClass.PinpointEmail,
    CampaignClass.SmartPinpointEmail,
    CampaignClass.Omni,
  ];

  if (!isEmailTrigger(optionsGroup)) {
    allowedClasses.push(CampaignClass.AutoPinpointEmail, CampaignClass.MailrailAutoSend);
  }

  return allowedClasses;
}

export function getValueInput({
  predicate,
  excludeIds,
  disabled,
  t,
  optionsGroup,
  handleChangeValue,
  selectedRelativeTimeOptionId,
  setSelectedRelativeTimeOptionId,
  relativeDateOptionsMap,
  user,
}: IInputParams): JSX.Element | null {
  const userTimezone = user.get('timezone');
  const { attr_against: targetAttribute, attr_type: type, comparison, kind } = predicate;

  function getPath(): string {
    const collectionMap = {
      activity: 'breadcrumbs',
      attribute: 'persons',
      update: 'persons',
    };
    return `${collectionMap[kind || 'activity']}/${targetAttribute}/values`;
  }

  function onSelect(selected: ICollectionOption | ICollectionOption[] | null): void {
    if (Array.isArray(selected)) {
      handleChangeValue(selected.map(({ id }) => id!));
    } else if (selected) {
      handleChangeValue(selected.id);
    } else {
      handleChangeValue(undefined);
    }
  }

  // Construct optional filters for 'campaign is' dropdown
  function getOptionalFilters(excludeIds: string[]): Record<string, string[]> {
    return excludeIds.length ? { id__nin: excludeIds } : {};
  }

  function getSelectProps(
    collection: TCollection,
    filters: Record<string, unknown> = {},
    multi = false,
  ): Record<string, unknown> {
    const idSelectShared = {
      disabled,
      key: `${predicate.key}_comparison`,
      name: 'predicate_value',
      onSelect,
      placeholder: t('Select an option'),
      value: predicate.value as string | undefined,
    };
    return {
      ...idSelectShared,
      collection: collection as TCollection,
      filters,
      multi,
    };
  }

  const idSelectProps = {
    cpn_id: getSelectProps('Campaigns', {
      _cls__in: getAllowedCampaignClasses(optionsGroup),
      ...getOptionalFilters(excludeIds),
    }),
    form_id: getSelectProps('Forms', undefined, ['in', 'nin'].includes(comparison ?? '')),
    e_id: getSelectProps('Events'),
    p_id: getSelectProps('Partners'),
    seg_id: getSelectProps('Segments', {
      context: 'breadcrumb',
      is_conversion_segment: true,
    }),
    tag_id: getSelectProps('Tags', {
      context: 'breadcrumb',
    }),
    tag_ids: getSelectProps(
      'Tags',
      {
        context: 'person',
      },
      true,
    ),
    'pp_opt_outs.events': getSelectProps('Events', {}, true),
    'pp_opt_outs.campaigns': getSelectProps(
      'Campaigns',
      {
        _cls__in: [
          CampaignClass.PinpointEmail,
          CampaignClass.SmartPinpointEmail,
          CampaignClass.AutoPinpointEmail,
        ],
      },
      true,
    ),
  };

  if (targetAttribute && Object.keys(idSelectProps).includes(targetAttribute)) {
    return <PredicateIdValueSelect {...idSelectProps[targetAttribute]} />;
  }

  const sharedInputProps = {
    disabled,
    key: `${predicate.key}_comparison`,
    name: 'predicate_value',
    placeholder: t('Enter a value'),
  };

  const relativeDateOptions: ISelectOption[] = Object.values(relativeDateOptionsMap);

  function handleChangeDateNumberValue(
    value?: number,
    relativeTimeOption: string | undefined = undefined,
  ): void {
    if (!value) {
      return;
    }

    runInAction(() => {
      // If days ago is the selected option and the the user has not changed the dropdown
      const isDaysAgo = selectedRelativeTimeOptionId === '-' && relativeTimeOption === undefined;

      /*
       * Check that if the user has selected a different relative time option because we aren't
       * getting the updated value of `selectedRelativeTimeOptionId` right away, probably because
       * `getValueInput` is outside of the component lifecycle.
       */
      const hasSelectedDaysAgo = relativeTimeOption === '-';

      // Use absolute value to convert the number to positive so we can negate it appropriately
      const formattedValue = isDaysAgo || hasSelectedDaysAgo ? -Math.abs(value) : Math.abs(value);

      handleChangeValue(formattedValue);
    });
  }

  function handleRelativeTimeSelect({ id }: ISelectOption): void {
    const option = relativeDateOptions.find((option) => option.id === id);
    if (!option) {
      return;
    }
    setSelectedRelativeTimeOptionId(option.id);

    // Negate the value if the dropdown has changed
    handleChangeDateNumberValue(predicate.value as number, option.id);
  }

  switch (type) {
    case 'string':
      return (
        <PredicateTextValueInput
          {...sharedInputProps}
          getPath={getPath}
          onChange={handleChangeValue}
          value={predicate.value as string}
        />
      );

    case 'list':
      return (
        <PredicateListValueSelect
          {...sharedInputProps}
          getPath={getPath}
          onChange={handleChangeValue}
          value={predicate.value as string[]}
        />
      );

    case 'date': {
      // Relative dates include `_r` in the comparison: eq_date_r, ne_date_r, gt_date_r, lt_date_r
      const isRelativeComparison = comparison?.includes('_r');
      // Exact date and time does not include `_` in the comparison: eq, ne, gt, lt
      const isExactDateAndTimeComparison = !comparison?.includes('_');
      const isTodaySelected = isRelativeComparison && predicate?.value === 0;
      if (isTodaySelected) {
        return null;
      }

      if (isRelativeComparison && predicate?.value !== 0) {
        const count = predicate.value !== undefined ? +predicate.value : undefined;
        const positiveCount = Math.abs(count ?? 0);
        const selectedOption = relativeDateOptions.find(
          (option) => option.id === selectedRelativeTimeOptionId,
        );

        return (
          <div className={styles.container}>
            <NumberInput
              {...sharedInputProps}
              className={styles.numberInput}
              min={1}
              onChange={handleChangeDateNumberValue}
              value={positiveCount}
              wrapperClassName={styles.numberInput}
            />
            <Select
              className={classNames(styles.select, styles.time)}
              disabled={disabled}
              name={'predicate_value_relative'}
              onChange={handleRelativeTimeSelect}
              options={relativeDateOptions}
              value={selectedOption}
            />
          </div>
        );
      }
      const selectedMoment = predicate.value ? moment.utc(predicate.value as string) : moment.utc();

      return (
        <DatePicker
          {...sharedInputProps}
          autoComplete={'off'}
          dateFormat={isExactDateAndTimeComparison ? 'MMMM d, yyyy h:mm aa' : 'MMMM d, yyyy'}
          disabled={isTodaySelected || disabled}
          // Prevent adding a min/max time since we can freely pick any date /time
          maxTime={undefined}
          minTime={undefined}
          onDateStrChange={handleChangeValue}
          placeholder={t('Select a date')}
          // Fix a bug that breaks the layout of the date picker when time is also shown
          showCalendarIcon={false}
          showTimeSelect={isExactDateAndTimeComparison}
          suffix={isExactDateAndTimeComparison && timezoneAbbr(selectedMoment.toDate())}
          timeIntervals={5}
          timezone={userTimezone}
          value={selectedMoment.format(TimeFormat.isoDateTime)}
        />
      );
    }

    case 'integer':

    case 'float':
      return (
        <NumberInput
          {...sharedInputProps}
          onChange={handleChangeValue}
          value={predicate.value as number | undefined}
          wrapperClassName={styles.numberInput}
        />
      );

    case 'boolean': {
      const booleanOptions: IBooleanOption[] = [
        { id: true, name: t('True') },
        { id: false, name: t('False') },
      ];

      function handleChangeBoolean({ id }: IBooleanOption): void {
        handleChangeValue(id);
      }

      return (
        <Select<IBooleanOption>
          {...sharedInputProps}
          onChange={handleChangeBoolean}
          options={booleanOptions}
          value={
            predicate.value !== undefined
              ? booleanOptions.find(({ id }) => predicate.value === id)
              : booleanOptions.find(({ id }) => false === id)
          }
        />
      );
    }

    default:
      return <Input {...sharedInputProps} onChange={handleChangeValue} type={'text'} />;
  }
}

function ValueInput({
  disabled = false,
  excludeIds = [],
  handleChangeValue,
  optionsGroup,
  predicate: { comparison },
  predicate,
}: IProps): JSX.Element | null {
  const { t } = useTranslation();
  const user = useUser();

  const relativeDateOptionsMap = getRelativeDateOptionsMap(t, predicate.value as number);
  const [selectedRelativeTimeOptionId, setSelectedRelativeTimeOptionId] = useState<string>(
    relativeDateOptionsMap.daysFromNow.id,
  );

  /*
   * The days-ago/days-from-now selection follows the stored value's sign — the
   * value can be replaced underneath this input (e.g. a date-type change resets
   * it to +1) without remounting it. Zero and non-numbers don't encode a
   * direction, so an explicit selection with no value yet keeps the user's choice.
   */
  useEffect(() => {
    if (
      predicate.attr_type === 'date' &&
      typeof predicate.value === 'number' &&
      predicate.value !== 0
    ) {
      setSelectedRelativeTimeOptionId(
        predicate.value < 0
          ? relativeDateOptionsMap.daysAgo.id
          : relativeDateOptionsMap.daysFromNow.id,
      );
    }
  }, [predicate.attr_type, predicate.value]);

  /*
   * We don't want to show value if there's no comparison,
   * or if we're just checking for existence.
   */
  if (!comparison || isExistenceComparison(comparison)) {
    return null;
  }

  return getValueInput({
    predicate,
    excludeIds,
    disabled,
    t,
    optionsGroup,
    handleChangeValue,
    selectedRelativeTimeOptionId,
    setSelectedRelativeTimeOptionId,
    relativeDateOptionsMap,
    user,
  });
}

export default observer(ValueInput);
